/**
 * The arrows out of one node are checked in the order they are stored; the
 * first that holds wins. Moving an arrow up or down changes its place among
 * the arrows out of the same node, and nothing else. `among` narrows the
 * siblings, as the pre-greeting arrows leave out "All other calls".
 */
export function moveAmongSiblings(arrows, id, delta, among = () => true) {
    const moving = arrows.find(arrow => arrow.id === id);
    if (!moving)
        return [...arrows];
    const slots = arrows
        .map((arrow, index) => arrow.source === moving.source && among(arrow) ? index : -1)
        .filter(index => index >= 0);
    const at = slots.findIndex(index => arrows[index].id === id);
    const to = at + delta;
    if (to < 0 || to >= slots.length)
        return [...arrows];
    const next = [...arrows];
    next[slots[at]] = arrows[slots[to]];
    next[slots[to]] = moving;
    return next;
}
/** A node's arrows and drafts in list order. A draft's `at` is the number of
 *  arrows above it; without one, it sits last. */
export function listOrder(arrows, drafts) {
    const count = arrows.length;
    const placeOf = (at) => Math.max(0, Math.min(at ?? count, count));
    const rows = [];
    for (let place = 0; place <= count; place += 1) {
        for (const draft of drafts) {
            if (placeOf(draft.at) === place) {
                rows.push({ id: draft.id, draft: true, row: draft });
            }
        }
        const arrow = arrows[place];
        if (arrow)
            rows.push({ id: arrow.id, draft: false, row: arrow });
    }
    return rows;
}
/** Moves the row at `from` to `to` in a listed order. */
export function moveRow(rows, from, to) {
    const next = [...rows];
    const [moving] = next.splice(from, 1);
    if (!moving)
        return [...rows];
    next.splice(Math.max(0, Math.min(to, next.length)), 0, moving);
    return next;
}
/** After a change to a node's list: the arrows' new order, and the drafts
 *  with each `at` reset. The node's drafts missing from `rows` are dropped. */
export function settleRows(drafts, source, rows, added = []) {
    const byId = new Map([...drafts, ...added]
        .filter(draft => draft.source === source)
        .map(draft => [draft.id, draft]));
    let above = 0;
    const own = [];
    for (const row of rows) {
        if (!row.draft) {
            above += 1;
            continue;
        }
        const draft = byId.get(row.id);
        if (draft)
            own.push({ ...draft, at: above });
    }
    return {
        order: rows.filter(row => !row.draft).map(row => row.id),
        drafts: [...drafts.filter(draft => draft.source !== source), ...own],
    };
}
/** Puts `source`'s arrows in `order`; other arrows keep their slots. */
export function orderSiblings(arrows, source, order) {
    const slots = arrows
        .map((arrow, index) => (arrow.source === source ? index : -1))
        .filter(index => index >= 0);
    const byId = new Map(arrows.map(arrow => [arrow.id, arrow]));
    const sorted = order.flatMap(id => {
        const arrow = byId.get(id);
        return arrow && arrow.source === source ? [arrow] : [];
    });
    if (sorted.length !== slots.length)
        return [...arrows];
    const next = [...arrows];
    slots.forEach((slot, i) => {
        next[slot] = sorted[i];
    });
    return next;
}
//# sourceMappingURL=arrow-order.js.map